/**
 * A simple in-memory monitor for logging security-related events.
 * This is useful for debugging and tracking security-sensitive actions during development.
 */
export class SecurityMonitor {
  private static events: Array<{
    type: string;
    timestamp: number;
    details: Record<string, unknown>;
  }> = [];

  /**
   * Logs a security event.
   * @param type A string identifying the type of event (e.g., 'XSS_DETECTED', 'CSRF_FAILURE').
   * @param details An object containing any relevant data about the event.
   */
  static logSecurityEvent(
    type: string,
    details: Record<string, unknown> = {}
  ): void {
    this.events.push({
      type,
      timestamp: Date.now(),
      details,
    });

    // Keep only the last 100 events to prevent memory leaks
    if (this.events.length > 100) {
      this.events = this.events.slice(-100);
    }

    // In development mode, log critical events to the console for visibility
    if (import.meta.env.DEV) {
      console.warn(`[Security Event] ${type}:`, details);
    }
  }

  /**
   * Retrieves all logged security events.
   * @returns An array of all stored security events.
   */
  static getEvents(): Array<{
    type: string;
    timestamp: number;
    details: Record<string, unknown>;
  }> {
    return this.events;
  }

  /**
   * Clears all logged security events.
   */
  static clearEvents(): void {
    this.events = [];
  }
}
